<template>
  <v-dialog
    :content-class="'add-new-authentication-object-popup-content'"
    :max-width='650'
    :value="visible"
    @click:outside='onCloseClickButton'
    @keydown.esc="onCloseClickButton"
  >
    <div class='add-new-authentication-object-popup'>
      <div class='add-new-authentication-object-popup__action'>
        <span class='close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class='add-new-authentication-object-popup__header'>
        <span class='header-label'>
          {{ title }}
        </span>
      </div>
      <div class='add-new-authentication-object-popup__form'>
        <CustomSelect
          class-name="type-select"
          v-model='authType'
          :options='items'
          :title="$t('add.new.authentication.object.popup.select.type.label').toString()"
          :placeholder="$t('add.new.authentication.object.popup.select.type.placeholder').toString()"
          size="small"
        />
        <div class='settings-field'>
          <span class='label'>{{ $t('add.new.authentication.object.popup.name.label') }}</span>
          <text-field class='text-field'
                      :placeholder="$t('add.new.authentication.object.popup.name.placeholder').toString()"
                      v-model='name'
          />
        </div>
        <div class='settings-field'>
          <span class='label'>{{ $t('add.new.authentication.object.popup.description.label') }}</span>
          <text-field class='text-field'
                      :placeholder="$t('add.new.authentication.object.popup.description.placeholder').toString()"
                      v-model='description'
          />
        </div>
        <div class='add-new-authentication-object-popup__form__credential-set'>
          <credential-set-input
            v-model='credentials'
            :visible='visible'
            :first-input='credentialInputs.first'
            :second-input='credentialInputs.second'
            :third-input='credentialInputs.third'
            :edit-mode='editMode'
          />
        </div>
      </div>
      <div class='add-new-authentication-object-popup__footer'>
        <div class="buttons">
          <v-btn small depressed class="btn-secondary cancel-button" @click="onCloseClickButton"> {{ $t('confirm.cancel') }} </v-btn>
          <v-btn class="btn-primary save-button" :disabled="saveButtonDisabled" @click="onSaveClick">
            {{ $t('save') }}
          </v-btn>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import CustomSelect from '@/components/common/Select.vue';
import {
  AUTHENTICATION_OBJECT_TYPES,
} from '@/constants/authenticationObject';
import CredentialSetInput from '@/components/common/CredentialSetInput.vue';
import TextField from '@/components/common/TextField.vue';
import { isEmptyString } from '@/utils/stringUtils';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AddNewAuthenticationObjectPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: { CredentialSetInput, CustomSelect, TextField },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    editData: {
      type: Object,
      required: false,
      default: null,
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      authType: AUTHENTICATION_OBJECT_TYPES.USER_PASSWORD,
      credentials: { first: '', second: '', third: ''},
      name: '',
      description: '',
    };
  },
  computed: {
    title() {
      return this.editMode ? this.$t('edit.authentication.object.popup.title') : this.$t('add.new.authentication.object.popup.title');
    },
    items() {
      return Object.values(AUTHENTICATION_OBJECT_TYPES).map(type => ({ value: type, title: this.$t(`auth.type.${type}`) }));
    },
    mandatoryInputsPerAuthType() {
      return {
        [AUTHENTICATION_OBJECT_TYPES.USER_PASSWORD]: ['first', 'second'],
        [AUTHENTICATION_OBJECT_TYPES.GCP]: ['first'],
        [AUTHENTICATION_OBJECT_TYPES.AWS]: ['first', 'second'],
        [AUTHENTICATION_OBJECT_TYPES.AZURE]: ['first', 'second', 'third'],
      };
    },
    credentialInputs() {
      const result = {
        first: { label: '', type: 'text'},
        second: { label: '', type: 'text'},
        third: { label: '', type: 'text'},
      };
      switch (this.authType) {
      case AUTHENTICATION_OBJECT_TYPES.USER_PASSWORD:
        result.first.label = 'auth.type.userPassword.firstLabel';
        result.first.type = 'text';
        result.second.label = 'auth.type.userPassword.secondLabel';
        result.second.type = 'password';
        delete result.third;
        break;
      case AUTHENTICATION_OBJECT_TYPES.AWS:
        result.first.label = 'auth.type.awsAuth.firstLabel';
        result.first.type = 'text';
        result.second.label = 'auth.type.awsAuth.secondLabel';
        result.second.type = 'password';
        delete result.third;
        break;
      case AUTHENTICATION_OBJECT_TYPES.GCP:
        result.first.label = 'auth.type.gcpAuth.firstLabel';
        result.first.type = 'jsonField';
        delete result.second;
        delete result.third;
        break;
      case AUTHENTICATION_OBJECT_TYPES.AZURE:
        result.first.label = 'auth.type.azureAuth.firstLabel';
        result.first.type = 'text';
        result.second.label = 'auth.type.azureAuth.secondLabel';
        result.second.type = 'text';
        result.third.label = 'auth.type.azureAuth.thirdLabel';
        result.third.type = 'password';
        break;
      default: break;
      }
      return result;
    },
    saveButtonDisabled() {
      return (isEmptyString(this.name) ||
        (this.mandatoryInputsPerAuthType[this.authType].some(inputNumber => isEmptyString(this.credentials[inputNumber]))) ||
        this.dataNotChanged);
    },
    dataNotChanged() {
      return (this.editData)
        ? (this.name === this.editData.name) &&
        (this.description === this.editData.description) &&
        (this.mandatoryInputsPerAuthType[this.authType].every(inputNumber => this.credentials[inputNumber] === this.editData.credentials[inputNumber])) &&
        (this.authType === this.editData.type)
        : false;
    },
  },
  methods: {
    onCloseClickButton() {
      this.clear();
      this.$emit('closed');
    },
    onSaveClick() {
      this.$emit('save', {
        type: this.authType,
        name: this.name,
        description: this.description,
        credentials: this.credentials,
        id: this.editData?.id || null,
      });
      this.clear();
    },
    clear() {
      this.authType = AUTHENTICATION_OBJECT_TYPES.USER_PASSWORD;
      this.credentials = { first: '', second: '', third: ''};
      this.name = '';
      this.description = '';
    },
  },
  watch: {
    editData: {
      handler() {
        this.name = this.editData?.name || '';
        this.description = this.editData?.description || '';
        this.credentials = { first: '', second: '', third: ''};
        this.credentials.first = this.editData?.credentials.first || '';
        this.credentials.second = this.editData?.credentials.second || '';
        this.credentials.third = this.editData?.credentials.third || '';
        this.authType = this.editData?.type || AUTHENTICATION_OBJECT_TYPES.USER_PASSWORD;
      },
      deep: true,
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.add-new-authentication-object-popup{
  min-height: rem(450);
  &__action {
    .close_btn {
      margin-right: $space-1-5;
      margin-top: calc(#{$space-2} + #{$space-0-5});
      float: right;

      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }
  }
  &__header{
    padding-top: rem(35);
    padding-left: rem(35);
    .header-label{
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: rem(18);
      font-weight: bold;
      line-height: $font-size-xl;
      letter-spacing: 0;
    }
  }
  &__form{
    margin-left: rem(35);
    margin-top: rem(10);
    .type-select.type-select {
      margin-right: rem(10);
      min-width: rem(180);
      width: rem(180);
      .el-input__inner {
        height: $space-8;
      }
    }
    &__credential-set {
      max-width: rem(400);
      .label {
        text-transform: uppercase;
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-4;
      }
    }
    .settings-field {
      margin-bottom: rem(10);
      padding-right: rem(35);
      .text-field.text-field{
        margin-top: $space-1-5;
      }
      .label {
        text-transform: uppercase;
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
      }
    }
  }
  &__footer {
    padding-bottom: $space-5;
    .buttons {
      text-align: right;
      padding-top: rem(30);
      padding-right: rem(22);

      .cancel-button {
        height: $space-8;
        border-radius: $radius-sm;
      }

      .save-button {
        height: $space-8;
        border-radius: $radius-sm;
        margin-left: $space-2;
      }
    }
  }
}
</style>
<style lang='scss'>
@import '~@/assets/styles/design-system';

.add-new-authentication-object-popup-content {
  // White popup background
  .v-card, > div {
    background-color: var(--bg-primary) !important;
  }
}

.add-new-authentication-object-popup {
  background-color: var(--bg-primary);

  .type-select span {
    text-transform: uppercase;
  }

  // White inputs inside this popup
  .el-input.el-input .el-input__inner {
    background: var(--bg-primary) !important;
  }
}
</style>
